import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, X } from "lucide-react";
import Modal from "components/common/Modal";

const TYPES = ["training", "match"];

/**
 * Rename a session and set its type (SPO-1538). Any coach can do this.
 * `onSave({ name, type })` returns a promise; the dialog stays open while
 * it runs and closes when it resolves.
 */
const EditSessionModal = ({ show, onClose, onSave, session }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [type, setType] = useState("training");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && session) {
      setName(session.name || "");
      setType(TYPES.includes(session.type) ? session.type : "training");
    }
  }, [show, session]);

  const trimmed = name.trim();
  const isValid = trimmed.length > 0;
  const hasChanged =
    trimmed !== (session?.name || "").trim() || type !== session?.type;

  const handleSave = async () => {
    if (!isValid || !hasChanged || saving) return;
    setSaving(true);
    try {
      await onSave?.({ name: trimmed, type });
      onClose();
    } catch {
      // The caller shows the error; keep the dialog open to retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onClose={onClose} maxWidth="440px">
      <div className="flex items-start justify-between border-b border-[#F3F4F6] px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#FEF3E7]">
            <Pencil size={16} className="text-[#E07A3A]" />
          </div>
          <h2 className="text-base font-semibold text-[#111827]">
            {t("session.editModal.title")}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.actions.close")}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] transition duration-150 hover:bg-[#F3F4F6]"
        >
          <X size={16} />
        </button>
      </div>

      <form
        className="space-y-5 px-6 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <div>
          <label
            htmlFor="edit-session-name"
            className="mb-2 block text-sm font-medium text-[#374151]"
          >
            {t("session.editModal.nameLabel")}
          </label>
          <input
            id="edit-session-name"
            type="text"
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] outline-none transition duration-150 focus:border-[#E07A3A] focus:ring-2 focus:ring-[#E07A3A]/10"
          />
        </div>

        <fieldset>
          <legend className="mb-2 block text-sm font-medium text-[#374151]">
            {t("session.editModal.typeLabel")}
          </legend>
          <div className="grid grid-cols-2 gap-1 rounded-[10px] bg-[#F3F4F6] p-1">
            {TYPES.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={type === value}
                onClick={() => setType(value)}
                className={`rounded-lg py-2 text-sm font-medium transition duration-150 ${
                  type === value
                    ? "bg-white text-[#111827] shadow-sm"
                    : "text-[#6B7280] hover:text-[#374151]"
                }`}
              >
                {t(`session.hero.types.${value}`)}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="flex justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
          >
            {t("common.actions.cancel")}
          </button>
          <button
            type="submit"
            disabled={!isValid || !hasChanged || saving}
            className="rounded-[10px] bg-[#E07A3A] px-5 py-2.5 text-sm font-semibold text-white transition duration-150 hover:bg-[#D06A2A] disabled:cursor-not-allowed disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]"
          >
            {saving ? t("session.editModal.saving") : t("common.actions.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditSessionModal;
